import isNil from 'lodash/isNil';
import {
  IBuilder,
  IQueryMeasureFormula,
  IRow,
  RowType,
} from '../../../../redux/slices/oldBuilder';
import {
  IBasicCompoundMeasureInput,
  IBasicSimpleMeasureInput,
  ICompoundMeasureInput,
  InputMaybe,
  IRollingControl,
  IRollingControlInput,
  ISimpleMeasureInput,
  ISimpleMeasurePropertiesInput,
  MeasureVersionType,
} from '../../../apollo/graphqlSchema';
import { parseEventConditionToInput } from '../../../../components/Builder/utils/builderUtils';
import { parseCohortRowsToCohortRefInput } from '../../cohorts';
import { getCharacterByIncrementedIndex } from '../../../character';
import { prepareMeasureFilterInput } from './prepareMeasureFilterInput';

export const prepareBasicSimpleMeasureInput = (
  row: Pick<IRow, 'fn' | 'eventConditions' | 'percentile'>,
  schemaId: string,
  measureFormula: IQueryMeasureFormula | null,
): IBasicSimpleMeasureInput => {
  const { fn, eventConditions, percentile } = row;

  const result: IBasicSimpleMeasureInput = {
    function: fn.key,
    eventConditions: eventConditions.map((eventCondition) =>
      parseEventConditionToInput(eventCondition, schemaId, true),
    ),
    percentile: null,
    formula: measureFormula?.expression ?? null,
  };

  if (fn.requirePercentile && !isNil(percentile)) {
    // XXX KB-6021 - display percentile number as a whole % instead of a fraction
    result.percentile = percentile / 100;
  }

  return result;
};

interface IPrepareSimpleMeasureInputArgs {
  row: Pick<IRow, 'fn' | 'eventConditions' | 'percentile' | 'measureId'>;
  rowIndex: number;
  measureFormula: IQueryMeasureFormula | null;
}

export const prepareSimpleMeasureInput = (
  args: IPrepareSimpleMeasureInputArgs,
  schemaId: string,
): ISimpleMeasureInput => {
  const { row, rowIndex, measureFormula } = args;
  const { measureId } = row;

  // Named(versioned) measure case:
  if (measureId) {
    return {
      basicMeasure: null,
      namedMeasure: {
        versionId: measureId,
      },
      type: MeasureVersionType.Named,
      varName: getCharacterByIncrementedIndex('A', rowIndex),
    };
  }

  return {
    basicMeasure: prepareBasicSimpleMeasureInput(row, schemaId, measureFormula),
    namedMeasure: null,
    type: MeasureVersionType.Basic,
    varName: getCharacterByIncrementedIndex('A', rowIndex),
  };
};

export const prepareRollingControlInput = (
  rollingControl: IRollingControl | undefined | null,
): InputMaybe<IRollingControlInput> => {
  if (!rollingControl || !rollingControl?.window) {
    return null;
  }

  return {
    function: rollingControl.function,
    window: rollingControl.window,
  };
};

type IPrepareBasicCompoundMeasureInputArgs = Pick<
  IBuilder,
  RowType.measures | 'measureFormula'
> & {
  schemaId: string;
};

export const prepareBasicCompoundMeasureInput = ({
  measures,
  measureFormula,
  schemaId,
}: IPrepareBasicCompoundMeasureInputArgs): IBasicCompoundMeasureInput => ({
  measures: measures.map((row, rowIndex) =>
    prepareSimpleMeasureInput(
      { row, rowIndex, measureFormula: null },
      schemaId,
    ),
  ),
  formula: measureFormula?.expression ?? null,
});

interface IPrepareCompoundMeasureInput
  extends IPrepareBasicCompoundMeasureInputArgs {
  versionId: string | null;
}

export const prepareCompoundMeasureInput = ({
  measures,
  measureFormula,
  versionId,
  schemaId,
}: IPrepareCompoundMeasureInput): ICompoundMeasureInput => {
  if (versionId) {
    return {
      basicMeasure: null,
      namedMeasure: {
        versionId,
      },
      type: MeasureVersionType.Named,
    };
  }

  return {
    basicMeasure: prepareBasicCompoundMeasureInput({
      measures,
      measureFormula,
      schemaId,
    }),
    namedMeasure: null,
    type: MeasureVersionType.Basic,
  };
};

export const prepareSimpleMeasurePropertiesInput = (
  row: IRow,
  schemaId: string,
): ISimpleMeasurePropertiesInput => {
  const { measureFilter, cohortFilter } = row;

  const cohortRefs =
    cohortFilter && cohortFilter?.isActive
      ? parseCohortRowsToCohortRefInput([cohortFilter], schemaId)
      : [];

  return {
    cohortRefs,
    filter: prepareMeasureFilterInput(measureFilter),
  };
};
